import { useState, useRef, useEffect } from 'react';
import { useLogout, useGetIdentity } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, ChevronDown, Settings, Shield, Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/Tooltip';

interface UserIdentity {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

const themeIcons = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const themeLabels = {
  light: 'Light mode',
  dark: 'Dark mode',
  system: 'System preference',
};

export function Header() {
  const { data: identity } = useGetIdentity<UserIdentity>();
  const { mutate: logout } = useLogout();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const ThemeIcon = themeIcons[theme];

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, []);

  return (
    <header className="flex h-16 items-center justify-between border-b bg-card px-6">
      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">
          Admin Panel
        </span>
      </div>

      <div className="flex items-center gap-4">
        {/* Theme toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={toggleTheme}
              className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted transition-colors"
              aria-label={`Current: ${themeLabels[theme]}. Click to toggle.`}
            >
              <ThemeIcon className="h-5 w-5 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{themeLabels[theme]}</TooltipContent>
        </Tooltip>

        {/* Profile dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted transition-colors"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <User className="h-4 w-4" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium">
                {identity?.name || 'Admin'}
              </span>
              <span className="text-xs text-muted-foreground">
                {identity?.email}
              </span>
            </div>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown menu */}
          {isMenuOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-lg border bg-card py-1 shadow-lg">
              {/* User info header */}
              <div className="px-4 py-3 border-b">
                <p className="text-sm font-medium">{identity?.name || 'Admin'}</p>
                <p className="text-xs text-muted-foreground truncate">{identity?.email}</p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    navigate('/settings');
                    setIsMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-muted transition-colors"
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>Profile</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/settings');
                    setIsMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-muted transition-colors"
                >
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span>Settings</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/security');
                    setIsMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-muted transition-colors"
                >
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span>Security</span>
                </button>
              </div>

              {/* Logout */}
              <div className="border-t py-1">
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    logout();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-destructive hover:bg-muted transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
